import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, MapPin, Ruler, Truck, Store, ShieldCheck, Loader2, Pencil, Package } from "lucide-react";
import api, { brl, mediaUrl, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Stars, Pill, LISTING_STATUS } from "@/components/market/MarketUI";
import { Loader, EmptyState } from "@/components/Loader";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

export default function ListingDetail() {
  const { id } = useParams();
  const [l, setL] = useState(undefined);
  const [img, setImg] = useState(0);

  useEffect(() => {
    api.get(`/market/listings/${id}`).then((r) => { setL(r.data); document.title = `${r.data.title} | Mercado PaleteLab`; }).catch(() => setL(null));
  }, [id]);

  if (l === undefined) return <Loader full />;
  if (l === null) return <EmptyState title="Anúncio não encontrado." action={<Button asChild className="rounded-full"><Link to="/mercado">Voltar ao Mercado</Link></Button>} />;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Link to="/mercado" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-market"><ChevronLeft className="h-4 w-4" /> Mercado</Link>
      <div className="mt-4 grid gap-8 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <div className="overflow-hidden rounded-2xl border border-border bg-secondary aspect-[4/3]">
            <img src={mediaUrl(l.images[img])} alt={l.title} className="h-full w-full object-cover" data-testid="listing-main-image" />
          </div>
          {l.images.length > 1 && (
            <div className="mt-3 flex gap-2 overflow-x-auto">
              {l.images.map((u, i) => (
                <button key={i} onClick={() => setImg(i)} className={`h-20 w-24 shrink-0 overflow-hidden rounded-xl border-2 ${i === img ? "border-primary" : "border-transparent"}`}>
                  <img src={mediaUrl(u)} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
          <div className="mt-8">
            <h2 className="text-xl font-bold">Descrição</h2>
            <p className="mt-3 whitespace-pre-line text-muted-foreground leading-relaxed" data-testid="listing-description">{l.description}</p>
          </div>
          <SellerCard seller={l.seller} />
        </div>

        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-20 rounded-2xl border border-border bg-card p-6">
            <div className="flex items-center gap-2">
              <span className="eyebrow text-primary">{l.kind === "movel" ? "Móvel pronto" : "Material"} · {l.condition}</span>
              {l.is_owner && <Pill map={LISTING_STATUS} value={l.status} testid="listing-status" />}
            </div>
            <h1 className="mt-2 text-2xl sm:text-3xl font-extrabold tracking-tight" data-testid="listing-title">{l.title}</h1>
            <p className="mt-3 text-3xl font-extrabold text-primary" data-testid="listing-price">{brl(l.price)}</p>
            <div className="mt-4 space-y-2 text-sm text-muted-foreground">
              <p className="flex items-center gap-2"><MapPin className="h-4 w-4" /> {l.city}/{l.state}</p>
              {l.dimensions && <p className="flex items-center gap-2"><Ruler className="h-4 w-4" /> {l.dimensions}</p>}
              <p className="flex items-center gap-2"><Package className="h-4 w-4" /> {l.stock} disponível(is)</p>
            </div>
            {l.is_owner ? (
              <Button asChild className="mt-6 w-full rounded-full gap-1.5" data-testid="edit-listing-btn"><Link to={`/mercado/editar/${l.id}`}><Pencil className="h-4 w-4" /> Editar anúncio</Link></Button>
            ) : l.status === "approved" ? <BuyBox listing={l} /> : <p className="mt-6 rounded-xl bg-secondary p-3 text-sm" data-testid="listing-unavailable">Anúncio indisponível para compra.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

function SellerCard({ seller }) {
  return (
    <Link to={`/mercado/vendedor/${seller.id}`} data-testid="seller-card" className="mt-8 flex items-center gap-4 rounded-2xl border border-border bg-card p-5 hover-lift">
      <img src={mediaUrl(seller.avatar)} alt="" className="h-14 w-14 rounded-xl object-cover" />
      <div className="flex-1 min-w-0">
        <p className="font-bold">{seller.store_name}</p>
        <p className="text-xs text-muted-foreground">{seller.city}/{seller.state}</p>
        <div className="mt-1 flex items-center gap-2"><Stars value={seller.rating} size="h-3.5 w-3.5" /><span className="text-xs text-muted-foreground">{seller.reviews_count ? `${seller.rating} (${seller.reviews_count})` : "Sem avaliações ainda"}</span></div>
      </div>
      <span className="text-sm font-semibold text-primary">Ver loja</span>
    </Link>
  );
}

function BuyBox({ listing: l }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [qty, setQty] = useState(1);
  const [mode, setMode] = useState(l.delivery ? "entrega" : "retirada");
  const [address, setAddress] = useState("");
  const [busy, setBusy] = useState(false);
  const shipping = mode === "entrega" ? l.delivery_fee || 0 : 0;
  const total = l.price * qty + shipping;

  const buy = async () => {
    if (!user) return navigate("/entrar");
    if (mode === "entrega" && address.trim().length < 10) return toast.error("Informe o endereço completo para entrega");
    setBusy(true);
    try {
      const { data } = await api.post("/market/checkout", { listing_id: l.id, quantity: qty, fulfillment: mode, address, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 space-y-4 border-t border-border pt-6">
      <div className="grid grid-cols-2 gap-2">
        {l.delivery && <ModeBtn active={mode === "entrega"} onClick={() => setMode("entrega")} icon={Truck} label="Entrega" sub={l.delivery_fee ? `+ ${brl(l.delivery_fee)}` : "Grátis"} testid="mode-entrega" />}
        {l.pickup && <ModeBtn active={mode === "retirada"} onClick={() => setMode("retirada")} icon={Store} label="Retirada" sub={`em ${l.city}`} testid="mode-retirada" />}
      </div>
      {mode === "entrega" && <Textarea data-testid="delivery-address" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Endereço completo de entrega (rua, número, bairro, cidade, CEP)" className="rounded-xl min-h-[80px]" />}
      <div className="flex items-center justify-between">
        <span className="text-sm">Quantidade</span>
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" size="icon" className="h-8 w-8 rounded-full hover:text-foreground" onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="qty-minus">−</Button>
          <span className="w-8 text-center font-mono" data-testid="qty-value">{qty}</span>
          <Button type="button" variant="outline" size="icon" className="h-8 w-8 rounded-full hover:text-foreground" onClick={() => setQty((q) => Math.min(l.stock, q + 1))} data-testid="qty-plus">+</Button>
        </div>
      </div>
      <div className="rounded-xl bg-secondary p-4 text-sm space-y-1">
        <div className="flex justify-between"><span>Produto</span><span>{brl(l.price * qty)}</span></div>
        <div className="flex justify-between"><span>Frete</span><span>{brl(shipping)}</span></div>
        <div className="flex justify-between pt-2 text-base font-extrabold"><span>Total</span><span data-testid="buy-total" className="text-primary">{brl(total)}</span></div>
      </div>
      <Button data-testid="buy-btn" onClick={buy} disabled={busy} className="h-12 w-full rounded-full text-base font-bold">
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : user ? "Comprar agora" : "Entre para comprar"}
      </Button>
      <p className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground"><ShieldCheck className="h-4 w-4" /> Pagamento seguro processado pela Stripe</p>
    </div>
  );
}

function ModeBtn({ active, onClick, icon: Icon, label, sub, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`rounded-xl border-2 p-3 text-left transition-colors ${active ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}>
      <Icon className="h-5 w-5 text-primary" />
      <p className="mt-1 text-sm font-bold">{label}</p>
      <p className="text-xs text-muted-foreground">{sub}</p>
    </button>
  );
}
